import { useState } from 'react';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import {
  areAllVisibleSelected,
  getUniqueIds,
  toggleSelection,
} from '@/utils/selection';

type UseSelectionOptions<TId extends string> = {
  initialIds?: readonly TId[];
  visibleIds?: readonly TId[];
};

type SelectionStoreState<TId extends string> = {
  clear: () => void;
  selectedIds: TId[];
  setSelectedIds: (ids: readonly TId[]) => void;
  toggle: (value: TId, selected: boolean) => void;
};

export type SelectionStore<TId extends string> = StoreApi<
  SelectionStoreState<TId>
>;

export { areAllVisibleSelected, getUniqueIds, toggleSelection };

function createSelectionStore<TId extends string>(
  initialIds: readonly TId[],
): SelectionStore<TId> {
  return createStore<SelectionStoreState<TId>>((set) => ({
    clear: () => {
      set({ selectedIds: [] });
    },
    selectedIds: getUniqueIds(initialIds),
    setSelectedIds: (ids) => {
      set({ selectedIds: getUniqueIds(ids) });
    },
    toggle: (value, selected) => {
      set((state) => ({
        selectedIds: toggleSelection({
          ids: state.selectedIds,
          selected,
          value,
        }),
      }));
    },
  }));
}

export default function useSelection<TId extends string>({
  initialIds = [],
  visibleIds = [],
}: UseSelectionOptions<TId> = {}) {
  const store = useSelectionStore({ initialIds });
  const selectedIds = useSelectionSelectedIds(store);
  const clear = useSelectionClear(store);
  const setSelectedIds = useSelectionSetSelectedIds(store);
  const toggle = useSelectionToggle(store);
  const selectedCount = selectedIds.length;
  const allVisibleSelected = areAllVisibleSelected({
    selectedIds,
    visibleIds,
  });
  const toggleVisible = (selected: boolean) => {
    setSelectedIds(selected ? visibleIds : []);
  };
  const isSelected = (value: TId) => selectedIds.includes(value);

  return {
    allVisibleSelected,
    clear,
    isSelected,
    selectedCount,
    selectedIds,
    setSelectedIds,
    store,
    toggle,
    toggleVisible,
  };
}

export function useSelectionStore<TId extends string>({
  initialIds = [],
}: Pick<UseSelectionOptions<TId>, 'initialIds'> = {}) {
  const [store] = useState<SelectionStore<TId>>(() =>
    createSelectionStore(initialIds),
  );

  return store;
}

export function useSelectionSelectedIds<TId extends string>(
  store: SelectionStore<TId>,
) {
  return useStore(store, (state) => state.selectedIds);
}

export function useSelectionIsSelected<TId extends string>({
  store,
  value,
}: {
  store: SelectionStore<TId>;
  value: TId;
}) {
  return useStore(store, (state) => state.selectedIds.includes(value));
}

export function useSelectionClear<TId extends string>(
  store: SelectionStore<TId>,
) {
  return useStore(store, (state) => state.clear);
}

export function useSelectionSetSelectedIds<TId extends string>(
  store: SelectionStore<TId>,
) {
  return useStore(store, (state) => state.setSelectedIds);
}

export function useSelectionToggle<TId extends string>(
  store: SelectionStore<TId>,
) {
  return useStore(store, (state) => state.toggle);
}
